import { useState, useCallback } from 'react'
import type { UpdateImage } from '@/types/update'

interface ImageGalleryProps {
  images: UpdateImage[]
  className?: string
}

export function ImageGallery({ images, className = '' }: ImageGalleryProps) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)

  const openLightbox = useCallback((index: number) => {
    setLightboxIndex(index)
  }, [])

  const closeLightbox = useCallback(() => {
    setLightboxIndex(null)
  }, [])

  const goToPrevious = useCallback(() => {
    setLightboxIndex((current) =>
      current !== null ? (current - 1 + images.length) % images.length : null
    )
  }, [images.length])

  const goToNext = useCallback(() => {
    setLightboxIndex((current) =>
      current !== null ? (current + 1) % images.length : null
    )
  }, [images.length])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (lightboxIndex === null) return

      switch (e.key) {
        case 'Escape':
          closeLightbox()
          break
        case 'ArrowLeft':
          goToPrevious()
          break
        case 'ArrowRight':
          goToNext()
          break
      }
    },
    [lightboxIndex, closeLightbox, goToPrevious, goToNext]
  )

  if (images.length === 0) {
    return null
  }

  // Grid layout based on number of images
  const gridClass =
    images.length === 1
      ? 'grid-cols-1'
      : images.length === 2
      ? 'grid-cols-2'
      : images.length <= 4
      ? 'grid-cols-2'
      : 'grid-cols-3'

  return (
    <>
      {/* Thumbnail grid - max-width to prevent huge images on desktop */}
      <div className={`grid gap-1 ${gridClass} max-w-sm ${className}`}>
        {images.map((image, index) => (
          <button
            key={image.id}
            type="button"
            onClick={() => openLightbox(index)}
            className={`relative overflow-hidden rounded-lg bg-gray-100 ${
              images.length === 1 ? 'aspect-video' : 'aspect-square'
            }`}
          >
            <img
              src={image.thumbnailUrl}
              alt=""
              className="h-full w-full object-cover transition-transform hover:scale-105"
              loading="lazy"
            />
            {/* Image count badge for more than 4 images */}
            {images.length > 4 && index === 3 && (
              <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
                <span className="text-xl font-semibold text-white">
                  +{images.length - 4}
                </span>
              </div>
            )}
          </button>
        ))}
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90"
          onClick={closeLightbox}
          onKeyDown={handleKeyDown}
          tabIndex={0}
          role="dialog"
          aria-modal="true"
        >
          {/* Close button */}
          <button
            type="button"
            onClick={closeLightbox}
            className="absolute right-4 top-4 z-10 rounded-full bg-black bg-opacity-50 p-2 text-white hover:bg-opacity-75"
            aria-label="Close"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

          {/* Navigation - Previous */}
          {images.length > 1 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                goToPrevious()
              }}
              className="absolute left-4 z-10 rounded-full bg-black bg-opacity-50 p-2 text-white hover:bg-opacity-75"
              aria-label="Previous image"
            >
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
          )}

          {/* Main image */}
          <div
            className="flex max-h-[90vh] max-w-[90vw] items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={images[lightboxIndex].fullUrl}
              alt=""
              className="max-h-[90vh] max-w-[90vw] object-contain"
            />
          </div>

          {/* Navigation - Next */}
          {images.length > 1 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                goToNext()
              }}
              className="absolute right-4 z-10 rounded-full bg-black bg-opacity-50 p-2 text-white hover:bg-opacity-75"
              aria-label="Next image"
            >
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          )}

          {/* Image counter */}
          {images.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-black bg-opacity-50 px-3 py-1 text-sm text-white">
              {lightboxIndex + 1} / {images.length}
            </div>
          )}
        </div>
      )}
    </>
  )
}
